import { format, isValid, parse } from 'date-fns';
import { LocationFilter, SortKey, StaffPerformanceRecord } from '../types';

const LOCALE = 'en-SG';
const isNum = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const round1 = (value: number): number => Math.round(value * 10) / 10;

// ---------------------------------------------------------------------------
// Names and labels
// ---------------------------------------------------------------------------

/** Prefers the mirrored display name, else derives "First Last" from "Last, First". */
export function formatDisplayName(record: Pick<StaffPerformanceRecord, 'display_name' | 'staff_name'>): string {
  const display = record.display_name?.trim();
  if (display) return display;
  const cleaned = (record.staff_name ?? '').trim().replace(/\s+/g, ' ');
  if (!cleaned) return 'Unknown staff';
  const comma = cleaned.indexOf(',');
  if (comma === -1) return cleaned;
  return [cleaned.slice(comma + 1).trim(), cleaned.slice(0, comma).trim()].filter(Boolean).join(' ');
}

/** "UFIT Singapore CBD, 21 Club St Spore 069410" → "Singapore CBD"; null → "No location". */
export function shortLocationName(location: string | null | undefined): string {
  if (!location) return 'No location';
  const beforeAddress = location.split(',')[0].trim();
  const withoutBrand = beforeAddress.replace(/^UFIT\s*@?\s*/i, '').trim();
  return withoutBrand || location;
}

/** "2026-04" → "Apr 26" */
export function monthLabel(month: string): string {
  const date = parse(month, 'yyyy-MM', new Date());
  return isValid(date) ? format(date, 'MMM yy') : month;
}

/** "2026-04 to 2026-09" → "Apr–Sep 2026"; "2025-10 to 2026-03" → "Oct 2025–Mar 2026". */
export function periodLabel(period: string | null | undefined): string {
  if (!period) return '-';
  const [startText, endText] = period.split(' to ').map((part) => part.trim());
  if (!startText || !endText) return period;
  const start = parse(startText, 'yyyy-MM', new Date());
  const end = parse(endText, 'yyyy-MM', new Date());
  if (!isValid(start) || !isValid(end)) return period;
  return start.getFullYear() === end.getFullYear()
    ? `${format(start, 'MMM')}–${format(end, 'MMM yyyy')}`
    : `${format(start, 'MMM yyyy')}–${format(end, 'MMM yyyy')}`;
}

export function formatInt(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 0 });
}

export function formatDecimal(value: number, decimals = 1): string {
  return value.toLocaleString(LOCALE, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** "+6", "−2" (true minus sign), "0". */
export function signed(value: number): string {
  if (value > 0) return `+${formatInt(value)}`;
  if (value < 0) return `−${formatInt(Math.abs(value))}`;
  return '0';
}

/** "+1.2%", "−8.5%", "0.0%" */
export function signedPct(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${formatDecimal(Math.abs(value))}%`;
}

// ---------------------------------------------------------------------------
// Deltas
// ---------------------------------------------------------------------------

export type Direction = 'up' | 'down' | 'flat' | 'na';

export interface Delta {
  direction: Direction;
  text: string;
}

const NA_DELTA: Delta = { direction: 'na', text: '-' };
const directionOf = (value: number): Direction => (value > 0 ? 'up' : value < 0 ? 'down' : 'flat');

export function pctDelta(value: number | null | undefined): Delta {
  if (!isNum(value)) return NA_DELTA;
  return { direction: directionOf(value), text: `${formatDecimal(Math.abs(value))}%` };
}

export function ptsDelta(value: number | null | undefined): Delta {
  if (!isNum(value)) return NA_DELTA;
  return { direction: directionOf(value), text: `${formatDecimal(Math.abs(value))} pts` };
}

export function countDelta(value: number | null | undefined, decimals = 1): Delta {
  if (!isNum(value)) return NA_DELTA;
  return { direction: directionOf(value), text: formatDecimal(Math.abs(value), decimals) };
}

// ---------------------------------------------------------------------------
// Team aggregates
// ---------------------------------------------------------------------------

export function isInactive(record: StaffPerformanceRecord): boolean {
  return record.sessions === null || record.sessions.latest.total === 0;
}

export interface TeamAggregates {
  staffCount: number;
  activeCount: number;
  sessionsLatest: number;
  sessionsPrior: number;
  sessionsChangePct: number | null;
  meanPerActiveCoachLatest: number | null;
  meanPerActiveCoachPrior: number | null;
  coachesWithClients: number;
  newLatest: number;
  nonReturningLatest: number;
  netLatest: number;
  netPrior: number;
  coachesWithConversion: number;
  convertedLatest: number;
  expiredLatest: number;
  convertedPrior: number;
  expiredPrior: number;
  rateLatest: number | null;
  ratePrior: number | null;
  rateChangePts: number | null;
}

/** Sums a set of staff records; families that are null are left out of their counts rather than counted as zero. */
export function aggregateTeam(records: StaffPerformanceRecord[]): TeamAggregates {
  let sessionsLatest = 0;
  let sessionsPrior = 0;
  let activeCount = 0;
  let meanSum = 0;
  let meanCount = 0;
  let priorMeanSum = 0;
  let priorMeanCount = 0;
  let coachesWithClients = 0;
  let newLatest = 0;
  let nonReturningLatest = 0;
  let netLatest = 0;
  let netPrior = 0;
  let coachesWithConversion = 0;
  let convertedLatest = 0;
  let expiredLatest = 0;
  let convertedPrior = 0;
  let expiredPrior = 0;

  for (const record of records) {
    const { sessions, clients, conversion } = record;
    if (sessions) {
      sessionsLatest += sessions.latest.total;
      sessionsPrior += sessions.prior.total;
      if (sessions.latest.total > 0) {
        activeCount += 1;
        if (isNum(sessions.latest.mean)) {
          meanSum += sessions.latest.mean;
          meanCount += 1;
        }
      }
      if (sessions.prior.total > 0 && isNum(sessions.prior.mean)) {
        priorMeanSum += sessions.prior.mean;
        priorMeanCount += 1;
      }
    }
    if (clients) {
      coachesWithClients += 1;
      newLatest += clients.latest.new;
      nonReturningLatest += clients.latest.nonReturning;
      netLatest += clients.latest.net;
      netPrior += clients.prior.net;
    }
    if (conversion) {
      coachesWithConversion += 1;
      convertedLatest += conversion.latest.converted;
      expiredLatest += conversion.latest.expired;
      convertedPrior += conversion.prior.converted;
      expiredPrior += conversion.prior.expired;
    }
  }

  const rate = (converted: number, expired: number): number | null =>
    expired > 0 ? round1((100 * converted) / expired) : null;
  const rateLatest = rate(convertedLatest, expiredLatest);
  const ratePrior = rate(convertedPrior, expiredPrior);

  return {
    staffCount: records.length,
    activeCount,
    sessionsLatest,
    sessionsPrior,
    sessionsChangePct: sessionsPrior > 0 ? round1(((sessionsLatest - sessionsPrior) / sessionsPrior) * 100) : null,
    meanPerActiveCoachLatest: meanCount > 0 ? round1(meanSum / meanCount) : null,
    meanPerActiveCoachPrior: priorMeanCount > 0 ? round1(priorMeanSum / priorMeanCount) : null,
    coachesWithClients,
    newLatest,
    nonReturningLatest,
    netLatest,
    netPrior,
    coachesWithConversion,
    convertedLatest,
    expiredLatest,
    convertedPrior,
    expiredPrior,
    rateLatest,
    ratePrior,
    rateChangePts: isNum(rateLatest) && isNum(ratePrior) ? round1(rateLatest - ratePrior) : null,
  };
}

// ---------------------------------------------------------------------------
// Sorting, filtering, grouping
// ---------------------------------------------------------------------------

function numericKey(record: StaffPerformanceRecord, key: SortKey): number | null {
  switch (key) {
    case 'sessions':
      return record.sessions?.latest.total ?? null;
    case 'change':
      return record.sessions?.meanChangePct ?? null;
    case 'net':
      return record.clients?.latest.net ?? null;
    case 'conversion':
      return record.conversion?.latest.ratePct ?? null;
    case 'overdue':
      return record.success_updates?.counts.overdue ?? null;
    default:
      return null;
  }
}

const byName = (a: StaffPerformanceRecord, b: StaffPerformanceRecord): number =>
  formatDisplayName(a).localeCompare(formatDisplayName(b));

/** Stable copy sorted for the key; numeric keys descend with nulls last. */
export function sortRecords(records: StaffPerformanceRecord[], key: SortKey): StaffPerformanceRecord[] {
  const copy = [...records];
  if (key === 'name') return copy.sort(byName);
  if (key === 'location') {
    return copy.sort(
      (a, b) =>
        shortLocationName(a.home_location).localeCompare(shortLocationName(b.home_location)) ||
        (b.sessions?.latest.total ?? -1) - (a.sessions?.latest.total ?? -1) ||
        byName(a, b)
    );
  }
  return copy.sort((a, b) => {
    const va = numericKey(a, key);
    const vb = numericKey(b, key);
    if (va === null && vb === null) return byName(a, b);
    if (va === null) return 1;
    if (vb === null) return -1;
    return vb - va || byName(a, b);
  });
}

export interface RecordFilters {
  search: string;
  showInactive: boolean;
  location: LocationFilter;
}

export function filterRecords(records: StaffPerformanceRecord[], filters: RecordFilters): StaffPerformanceRecord[] {
  const query = filters.search.trim().toLowerCase();
  return records.filter((record) => {
    if (!filters.showInactive && isInactive(record)) return false;
    if (filters.location === 'NONE') {
      if (record.home_location !== null) return false;
    } else if (filters.location !== 'ALL' && record.home_location !== filters.location) {
      return false;
    }
    if (!query) return true;
    return (
      formatDisplayName(record).toLowerCase().includes(query) ||
      (record.staff_name ?? '').toLowerCase().includes(query) ||
      String(record.staff_id).includes(query)
    );
  });
}

export interface LocationGroup {
  location: string | null;
  label: string;
  records: StaffPerformanceRecord[];
}

/** Groups by home location, largest group first, staff with no location last. */
export function groupByLocation(records: StaffPerformanceRecord[]): LocationGroup[] {
  const groups = new Map<string | null, StaffPerformanceRecord[]>();
  for (const record of records) {
    const existing = groups.get(record.home_location);
    if (existing) existing.push(record);
    else groups.set(record.home_location, [record]);
  }
  return Array.from(groups.entries())
    .map(([location, members]) => ({ location, label: shortLocationName(location), records: members }))
    .sort((a, b) => {
      if (a.location === null) return 1;
      if (b.location === null) return -1;
      return b.records.length - a.records.length || a.label.localeCompare(b.label);
    });
}

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
const RUN_HOUR_SGT = 6;

/** Next weekly (Monday 06:00) and monthly (1st 06:00) runs in Asia/Singapore, as UTC instants. */
export function nextScheduledRuns(from: Date): { weekly: Date; monthly: Date } {
  const wall = new Date(from.getTime() + SGT_OFFSET_MS);
  const year = wall.getUTCFullYear();
  const month = wall.getUTCMonth();
  const day = wall.getUTCDate();
  const runAt = (y: number, m: number, d: number): Date => new Date(Date.UTC(y, m, d, RUN_HOUR_SGT) - SGT_OFFSET_MS);

  let daysAhead = (1 - wall.getUTCDay() + 7) % 7;
  if (daysAhead === 0 && from.getTime() >= runAt(year, month, day).getTime()) daysAhead = 7;
  const weekly = runAt(year, month, day + daysAhead);

  const thisFirst = runAt(year, month, 1);
  const monthly = from.getTime() < thisFirst.getTime() ? thisFirst : runAt(year, month + 1, 1);

  return { weekly, monthly };
}
